/**
 * Composable for Telnyx WebRTC calling.
 *
 * Provides browser-based audio calling functionality using the Telnyx WebRTC SDK.
 * Used for officer connection to hearing conferences via browser audio.
 */

import { ref, computed, onUnmounted, type Ref } from 'vue';

// Types for Telnyx WebRTC
// Note: @telnyx/webrtc package provides these types when installed
export interface TelnyxRTCOptions {
  login_token?: string;
  login?: string;
  password?: string;
  ringtoneFile?: string;
  ringbackFile?: string;
}

export interface TelnyxCall {
  id: string;
  state: string;
  direction: 'inbound' | 'outbound';
  answer: () => void;
  hangup: () => void;
  muteAudio: () => void;
  unmuteAudio: () => void;
  hold: () => void;
  unhold: () => void;
  dtmf: (digit: string) => void;
}

export interface WebRTCState {
  isInitialized: boolean;
  isConnecting: boolean;
  isConnected: boolean;
  isInCall: boolean;
  isMuted: boolean;
  isOnHold: boolean;
  callState: 'idle' | 'connecting' | 'ringing' | 'active' | 'held' | 'ended' | 'error';
  error: string | null;
  callDuration: number;
}

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

export interface WebRTCCredentials {
  token: string;
  identity: string;
  expires_at: string;
  sip_uri: string;
  ice_servers?: Array<{ urls: string }>;
  hearing_id?: string;
  conference_id?: string;
  caller_name?: string;  // Tenant name for caller ID
  caller_number?: string;  // Telnyx DID in E.164 format
}

export interface CallOptions {
  callerName?: string;  // Display name for caller ID
  callerNumber?: string;  // Caller's phone number (E.164 format)
  clientState?: Record<string, any>;  // Metadata for webhook
}

/**
 * Convert SIP URI to E.164 phone number format
 * @param sipUri - SIP URI like "sip:+15551234567@sip.telnyx.com" or just a phone number
 * @returns E.164 formatted number like "+15551234567"
 */
export function sipToE164(sipUri: string): string {
  if (!sipUri) return '';

  // If already in E.164 format (starts with +), return as-is
  if (sipUri.startsWith('+')) {
    // Remove any non-digit characters except the leading +
    return '+' + sipUri.slice(1).replace(/\D/g, '');
  }

  // Extract phone number from SIP URI format: sip:+15551234567@sip.telnyx.com
  const sipMatch = sipUri.match(/sip:([+\d]+)@/i);
  if (sipMatch) {
    const number = sipMatch[1];
    // Ensure it starts with +
    return number.startsWith('+') ? number : `+${number}`;
  }

  // Try to extract just digits and format as E.164
  const digits = sipUri.replace(/\D/g, '');
  if (digits.length >= 10) {
    // Assume US number if 10 digits, otherwise keep as-is with +
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  }

  // Return original if we can't parse it
  return sipUri;
}

// Dynamically import Telnyx WebRTC SDK
let TelnyxRTC: any = null;

async function loadTelnyxSDK(): Promise<any> {
  if (TelnyxRTC) return TelnyxRTC;

  try {
    const module = await import('@telnyx/webrtc');
    TelnyxRTC = module.TelnyxRTC;
    return TelnyxRTC;
  } catch (e) {
    console.error('[WebRTC] Failed to load Telnyx SDK:', e);
    throw new Error('Telnyx WebRTC SDK not available. Please install @telnyx/webrtc');
  }
}

// ============================================================================
// SHARED STATE (module-level singleton - shared across all components)
// This ensures the WebRTC client and call state persist when navigating
// between FloatingDialer and Dialer.vue
// ============================================================================
const client: Ref<any> = ref(null);
const currentCall: Ref<TelnyxCall | null> = ref(null);
const callDurationInterval: Ref<ReturnType<typeof setInterval> | null> = ref(null);
const storedCredentials: Ref<WebRTCCredentials | null> = ref(null);

const state = ref<WebRTCState>({
  isInitialized: false,
  isConnecting: false,
  isConnected: false,
  isInCall: false,
  isMuted: false,
  isOnHold: false,
  callState: 'idle',
  error: null,
  callDuration: 0,
});

// Audio device state
const audioInputDevices: Ref<AudioDevice[]> = ref([]);
const audioOutputDevices: Ref<AudioDevice[]> = ref([]);
const selectedAudioInput: Ref<string> = ref('');
const selectedAudioOutput: Ref<string> = ref('');
const isLoadingDevices: Ref<boolean> = ref(false);

// Load saved audio preferences from localStorage
function loadSavedAudioPreferencesOnInit(): void {
  const savedInput = localStorage.getItem('webrtc_audio_input');
  const savedOutput = localStorage.getItem('webrtc_audio_output');
  if (savedInput) selectedAudioInput.value = savedInput;
  if (savedOutput) selectedAudioOutput.value = savedOutput;
}
loadSavedAudioPreferencesOnInit();
// ============================================================================

export function useTelnyxWebRTC() {

  // Computed
  const isReady = computed(() => state.value.isInitialized && state.value.isConnected);
  const canCall = computed(() => isReady.value && !state.value.isInCall);

  // Get the remote caller number in E.164 format (extracted from SIP URI if needed)
  const remoteCallerNumber = computed(() => {
    if (!currentCall.value) return null;
    // Telnyx SDK provides remote number in various formats - normalize to E.164
    const remoteNumber = (currentCall.value as any).options?.remoteCallerNumber
      || (currentCall.value as any).options?.destinationNumber
      || (currentCall.value as any).remoteCallerNumber;
    return remoteNumber ? sipToE164(remoteNumber) : null;
  });

  /**
   * Initialize the Telnyx WebRTC client with credentials
   */
  async function initialize(credentials: WebRTCCredentials): Promise<void> {
    if (state.value.isInitialized) {
      console.warn('[WebRTC] Already initialized');
      return;
    }

    state.value.isConnecting = true;
    state.value.error = null;

    // Store credentials for later use (e.g., getting hearing_id)
    storedCredentials.value = credentials;

    try {
      const SDK = await loadTelnyxSDK();

      // v2.4.31 — credentials.token is a Telnyx JWT from
      // POST /telephony_credentials/{id}/token (three base64url segments
      // separated by dots). Pass it through as login_token — matches how
      // agent-desktop authenticates (agent-desktop/src/composables/
      // useTelnyxWebRTC.js:45). The prior atob(token) / username:password
      // split was left over from an older SIP-credential auth mode and
      // threw "The string to be decoded is not correctly encoded" against
      // real JWTs (the '.' separator is not valid base64).
      client.value = new SDK({
        login_token: credentials.token,
        // Optional: configure ringtones
        // ringtoneFile: '/sounds/ringtone.mp3',
        // ringbackFile: '/sounds/ringback.mp3',
      });

      // v2.4.33 — point the SDK at a root-level <audio> element so remote
      // audio actually renders. Without this, the SDK connects but there's
      // no MediaStream sink → officer hears nothing. Every portal that
      // uses this composable MUST mount <audio id="telnyx-remote-audio"
      // autoplay> once at its app root (see agent-desktop's AppShell.vue:14
      // for the reference pattern). ID lookup — the SDK does the DOM
      // resolve internally when a call becomes active.
      client.value.remoteElement = 'telnyx-remote-audio';

      // Set up event handlers
      setupEventHandlers();

      // Connect to Telnyx and wait for ready event
      await client.value.connect();

      // Wait for the telnyx.ready event before considering initialization complete
      await waitForReady(10000); // 10 second timeout

      state.value.isInitialized = true;
      console.log('[WebRTC] Initialized and ready');
    } catch (e: any) {
      console.error('[WebRTC] Initialization failed:', e);
      state.value.error = e.message || 'Failed to initialize WebRTC';
      state.value.isConnecting = false;
      throw e;
    }
  }

  /**
   * Wait for the WebRTC client to be ready
   */
  function waitForReady(timeoutMs: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      // If already connected, resolve immediately
      if (state.value.isConnected) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('WebRTC connection timeout - client did not become ready'));
      }, timeoutMs);

      // Set up a one-time listener for the ready event
      const checkReady = () => {
        if (state.value.isConnected) {
          clearTimeout(timeout);
          resolve();
        }
      };

      // Poll for connected state (the event handler will set it)
      const interval = setInterval(() => {
        if (state.value.isConnected) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve();
        }
      }, 100);

      // Also clear interval on timeout
      setTimeout(() => clearInterval(interval), timeoutMs);
    });
  }

  /**
   * Set up event handlers for the Telnyx client
   */
  function setupEventHandlers(): void {
    if (!client.value) return;

    client.value.on('telnyx.ready', () => {
      console.log('[WebRTC] Client ready');
      state.value.isConnected = true;
      state.value.isConnecting = false;
    });

    client.value.on('telnyx.error', (error: any) => {
      console.error('[WebRTC] Client error:', error);
      state.value.error = error.message || 'WebRTC error';
      state.value.callState = 'error';
    });

    client.value.on('telnyx.socket.close', () => {
      console.log('[WebRTC] Socket closed');
      state.value.isConnected = false;
    });

    client.value.on('telnyx.notification', handleNotification);
  }

  /**
   * Handle incoming notifications from Telnyx
   */
  function handleNotification(notification: any): void {
    const { call } = notification;
    if (!call) return;

    console.log('[WebRTC] Call notification:', call.state, call);

    // Update current call reference
    currentCall.value = call;

    switch (call.state) {
      case 'new':
      case 'trying':
        state.value.callState = 'connecting';
        state.value.isInCall = true;
        break;

      case 'early':
      case 'ringing':
        state.value.callState = 'ringing';
        break;

      case 'active':
        state.value.callState = 'active';
        state.value.isInCall = true;
        startCallDurationTimer();
        break;

      case 'held':
        console.warn('[WebRTC] Call state changed to HELD - officer WebRTC leg is on hold');
        state.value.callState = 'held';
        state.value.isOnHold = true;
        break;

      case 'destroy':
      case 'hangup':
      case 'purge':
      case 'bye':  // Remote hangup
        console.log('[WebRTC] Call ended, state:', call.state);
        state.value.callState = 'ended';
        state.value.isInCall = false;
        state.value.isMuted = false;
        state.value.isOnHold = false;
        currentCall.value = null;
        stopCallDurationTimer();
        break;
    }
  }

  /**
   * Start timer for call duration
   */
  function startCallDurationTimer(): void {
    stopCallDurationTimer();
    state.value.callDuration = 0;
    callDurationInterval.value = setInterval(() => {
      state.value.callDuration++;
    }, 1000);
  }

  /**
   * Stop call duration timer
   */
  function stopCallDurationTimer(): void {
    if (callDurationInterval.value) {
      clearInterval(callDurationInterval.value);
      callDurationInterval.value = null;
    }
  }

  /**
   * Make a call to a destination (SIP URI or phone number)
   * @param destination - The SIP URI or phone number to call
   * @param options - Optional call options including caller ID and metadata
   */
  async function call(destination: string, options?: CallOptions): Promise<void> {
    if (!client.value || !state.value.isConnected) {
      throw new Error('WebRTC client not connected');
    }

    if (state.value.isInCall) {
      throw new Error('Already in a call');
    }

    state.value.callState = 'connecting';
    state.value.error = null;

    try {
      // For SIP URIs, keep the full URI to route through Telnyx properly
      // For phone numbers, format to E.164
      let formattedDestination: string;
      if (destination.toLowerCase().startsWith('sip:')) {
        // Keep SIP URI format - required for webhook routing
        formattedDestination = destination;
      } else {
        // Convert to E.164 format for direct phone calls
        formattedDestination = sipToE164(destination);
      }

      // Build call options
      const callParams: any = {
        destinationNumber: formattedDestination,
        audio: true,
        video: false,
      };

      // Add caller ID name if provided (or from stored credentials)
      const callerName = options?.callerName || storedCredentials.value?.caller_name;
      if (callerName) {
        callParams.callerName = callerName;
      }

      // Add caller ID number if provided (or from stored credentials)
      const callerNumber = options?.callerNumber || storedCredentials.value?.caller_number;
      if (callerNumber) {
        callParams.callerNumber = callerNumber;
      }

      // Add client state if provided (base64 encoded JSON for Telnyx webhook)
      if (options?.clientState) {
        callParams.clientState = btoa(JSON.stringify(options.clientState));
      }

      console.log('[WebRTC] Making call to:', formattedDestination, 'callerName:', callerName, 'callerNumber:', callerNumber, 'clientState:', options?.clientState);
      console.log('[WebRTC] Call params:', JSON.stringify(callParams, null, 2));
      currentCall.value = client.value.newCall(callParams);
    } catch (e: any) {
      console.error('[WebRTC] Failed to make call:', e);
      state.value.error = e.message || 'Failed to make call';
      state.value.callState = 'error';
      throw e;
    }
  }

  /**
   * Join a conference by SIP URI
   * Automatically includes hearing_id from stored credentials in the client state
   * Uses caller_name and caller_number from credentials for caller ID
   */
  async function joinConference(
    sipUri: string,
    hearingId?: string,
    extraClientState?: Record<string, any>,
  ): Promise<void> {
    console.log('[WebRTC] Joining conference:', sipUri);

    // Build client state with hearing info for the webhook.
    // core/v2.7.76 — accept an optional ``extraClientState`` so callers
    // (DARS officer portal, other solutions) can inject solution-specific
    // context (e.g. ``dars_hearing_id`` + ``dars_participant_id``) without
    // hard-coding those keys into this shared composable. Extra keys are
    // spread first so the shared defaults below (initiated_from, role,
    // hearing_id, conference_id) always win if there's a collision.
    const clientState: Record<string, any> = {
      ...(extraClientState || {}),
      initiated_from: 'webrtc',
      role: 'officer',
    };

    // Use provided hearingId or fall back to stored credentials
    const effectiveHearingId = hearingId || storedCredentials.value?.hearing_id;
    if (effectiveHearingId) {
      clientState.hearing_id = effectiveHearingId;
    }

    if (storedCredentials.value?.conference_id) {
      clientState.conference_id = storedCredentials.value.conference_id;
    }

    // Build call options with caller ID from credentials
    const options: CallOptions = {
      callerName: storedCredentials.value?.caller_name,
      callerNumber: storedCredentials.value?.caller_number,
      clientState,
    };

    await call(sipUri, options);
  }

  /**
   * Hang up the current call
   */
  function hangup(): void {
    if (currentCall.value) {
      try {
        currentCall.value.hangup();
      } catch (e) {
        console.error('[WebRTC] Error hanging up:', e);
      }
    }
    state.value.isInCall = false;
    state.value.callState = 'ended';
    currentCall.value = null;
    stopCallDurationTimer();
  }

  /**
   * Toggle mute on current call
   */
  function toggleMute(): void {
    if (!currentCall.value) return;

    try {
      if (state.value.isMuted) {
        currentCall.value.unmuteAudio();
      } else {
        currentCall.value.muteAudio();
      }
      state.value.isMuted = !state.value.isMuted;
    } catch (e) {
      console.error('[WebRTC] Error toggling mute:', e);
    }
  }

  /**
   * Mute the call
   */
  function mute(): void {
    if (!currentCall.value || state.value.isMuted) return;
    try {
      currentCall.value.muteAudio();
      state.value.isMuted = true;
    } catch (e) {
      console.error('[WebRTC] Error muting:', e);
    }
  }

  /**
   * Unmute the call
   */
  function unmute(): void {
    if (!currentCall.value || !state.value.isMuted) return;
    try {
      currentCall.value.unmuteAudio();
      state.value.isMuted = false;
    } catch (e) {
      console.error('[WebRTC] Error unmuting:', e);
    }
  }

  /**
   * Toggle hold on current call
   */
  function toggleHold(): void {
    if (!currentCall.value) return;

    try {
      if (state.value.isOnHold) {
        currentCall.value.unhold();
      } else {
        currentCall.value.hold();
      }
      state.value.isOnHold = !state.value.isOnHold;
    } catch (e) {
      console.error('[WebRTC] Error toggling hold:', e);
    }
  }

  /**
   * Send DTMF tone
   */
  function sendDTMF(digit: string): void {
    if (!currentCall.value) return;
    try {
      currentCall.value.dtmf(digit);
    } catch (e) {
      console.error('[WebRTC] Error sending DTMF:', e);
    }
  }

  /**
   * Load available audio devices (microphones and speakers)
   * Can be called before initialization to enumerate devices
   */
  async function loadAudioDevices(): Promise<void> {
    isLoadingDevices.value = true;
    try {
      // First request microphone permission to get device labels
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const devices = await navigator.mediaDevices.enumerateDevices();

      audioInputDevices.value = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.substring(0, 8)}`,
          kind: 'audioinput' as const,
        }));

      audioOutputDevices.value = devices
        .filter(d => d.kind === 'audiooutput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.substring(0, 8)}`,
          kind: 'audiooutput' as const,
        }));

      console.log('[WebRTC] Audio devices loaded:', {
        inputs: audioInputDevices.value.length,
        outputs: audioOutputDevices.value.length,
      });

      // Set defaults if not already set
      if (!selectedAudioInput.value && audioInputDevices.value.length > 0) {
        selectedAudioInput.value = audioInputDevices.value[0].deviceId;
      }
      if (!selectedAudioOutput.value && audioOutputDevices.value.length > 0) {
        selectedAudioOutput.value = audioOutputDevices.value[0].deviceId;
      }
    } catch (e: any) {
      console.error('[WebRTC] Error loading audio devices:', e);
      // If permission denied, still try to enumerate without labels
      if (e.name === 'NotAllowedError') {
        const devices = await navigator.mediaDevices.enumerateDevices();
        audioInputDevices.value = devices
          .filter(d => d.kind === 'audioinput')
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: `Microphone ${i + 1}`,
            kind: 'audioinput' as const,
          }));
        audioOutputDevices.value = devices
          .filter(d => d.kind === 'audiooutput')
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: `Speaker ${i + 1}`,
            kind: 'audiooutput' as const,
          }));
      }
    } finally {
      isLoadingDevices.value = false;
    }
  }

  /**
   * Get available microphones
   */
  async function getAudioInputDevices(): Promise<AudioDevice[]> {
    if (audioInputDevices.value.length === 0) {
      await loadAudioDevices();
    }
    return audioInputDevices.value;
  }

  /**
   * Get available speakers
   */
  async function getAudioOutputDevices(): Promise<AudioDevice[]> {
    if (audioOutputDevices.value.length === 0) {
      await loadAudioDevices();
    }
    return audioOutputDevices.value;
  }

  /**
   * Set the microphone to use
   * @param deviceId - The device ID of the microphone
   */
  async function setAudioInputDevice(deviceId: string): Promise<void> {
    selectedAudioInput.value = deviceId;
    console.log('[WebRTC] Setting audio input device:', deviceId);

    // If we have an active call or client, update the device
    if (client.value && typeof client.value.setAudioSettings === 'function') {
      try {
        await client.value.setAudioSettings({
          micId: deviceId,
        });
        console.log('[WebRTC] Audio input device updated');
      } catch (e) {
        console.error('[WebRTC] Error setting audio input device:', e);
      }
    }

    // Store in localStorage for persistence
    localStorage.setItem('webrtc_audio_input', deviceId);
  }

  /**
   * Set the speaker to use
   * @param deviceId - The device ID of the speaker
   */
  async function setAudioOutputDevice(deviceId: string): Promise<void> {
    selectedAudioOutput.value = deviceId;
    console.log('[WebRTC] Setting audio output device:', deviceId);

    // If we have an active call or client, update the device
    if (client.value && typeof client.value.setAudioSettings === 'function') {
      try {
        await client.value.setAudioSettings({
          speakerId: deviceId,
        });
        console.log('[WebRTC] Audio output device updated');
      } catch (e) {
        console.error('[WebRTC] Error setting audio output device:', e);
      }
    }

    // Store in localStorage for persistence
    localStorage.setItem('webrtc_audio_output', deviceId);
  }

  // Note: Audio preferences are loaded at module level (loadSavedAudioPreferencesOnInit)

  /**
   * Disconnect and cleanup the WebRTC client
   */
  function disconnect(): void {
    hangup();
    stopCallDurationTimer();

    if (client.value) {
      try {
        client.value.disconnect();
      } catch (e) {
        console.error('[WebRTC] Error disconnecting:', e);
      }
      client.value = null;
    }

    storedCredentials.value = null;
    state.value.isInitialized = false;
    state.value.isConnected = false;
    state.value.isConnecting = false;
    state.value.callState = 'idle';
    state.value.error = null;
  }

  /**
   * Format call duration as MM:SS or HH:MM:SS
   */
  function formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    // State
    state,
    currentCall,
    isReady,
    canCall,
    credentials: storedCredentials,
    remoteCallerNumber,

    // Audio device state
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    isLoadingDevices,

    // Methods
    initialize,
    call,
    joinConference,
    hangup,
    toggleMute,
    mute,
    unmute,
    toggleHold,
    sendDTMF,
    disconnect,

    // Audio device methods
    loadAudioDevices,
    getAudioInputDevices,
    getAudioOutputDevices,
    setAudioInputDevice,
    setAudioOutputDevice,

    // Utilities
    formatDuration,
    sipToE164,
  };
}
